import React from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import supabase from "../../utils/supabase";
import { getCourseTypesForState } from "../../config/courseTypes";

interface EditDistrictModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly district: {
    id: string;
    name: string;
    user_id: string;
    created_at: string;
    course_type?: string | null;
    state?: {
      id: number;
      name: string;
    } | null;
  } | null;
  readonly onSuccess?: () => void;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("District name is required")
    .min(2, "District name must be at least 2 characters"),
});

export default function EditDistrictModal({
  isOpen,
  onClose,
  district,
  onSuccess,
}: EditDistrictModalProps) {
  if (!district) return null;

  const handleSubmit = async (
    values: { name: string },
    { setSubmitting, resetForm }: any
  ) => {
    try {
      // Only update the name, state is readonly
      const updateData: { name: string } = {
        name: values.name,
      };

      const { error } = await supabase
        .from("districts")
        .update(updateData)
        .eq("id", district.id);

      if (error) {
        console.error("Error updating district:", error);
        toast.error("Failed to update district");
        return;
      }

      toast.success("District updated successfully!");
      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating district:", error);
      toast.error("Failed to update district");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit District">
      <Formik
        initialValues={{
          name: district.name,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                District Name
              </label>
              <Field
                id="name"
                name="name"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter district name"
              />
              {formik.errors.name && formik.touched.name && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>

            <div>
              <div className="block text-sm font-medium text-gray-700 mb-1">
                State
              </div>
              <div className="text-sm text-gray-900">{district.state?.name}</div>
            </div>

            {district.course_type && district.state?.name && (() => {
              const courseTypes = getCourseTypesForState(district.state.name);
              const current = courseTypes?.find((ct) => ct.value === district.course_type);
              return current ? (
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-1">
                    Course Type
                  </div>
                  <div className="text-sm text-gray-900">{current.label}</div>
                </div>
              ) : null;
            })()}

            <div className="pt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-small btn-secondary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-small ${
                  formik.isSubmitting ? "btn-disabled" : ""
                }`}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting ? "Updating..." : "Update District"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
